/*
  Fumetsu tiered kit builder UI. Native DOM only.
  Tier logic lives in fumetsu-kit-tiers.js. This file loads the real catalogue, tracks the
  selection and posts the chosen variants to the normal cart. It never sets a price.
*/
(function () {
  'use strict';

  var Kit = window.FumetsuKitTiers;
  var PAGE_SIZE = 24;

  function init(root) {
    if (!Kit || root.kitReady) return;
    root.kitReady = true;

    function $(selector, scope) {
      return (scope || root).querySelector(selector);
    }

    var settings = JSON.parse($('[data-kit-config]').textContent);
    var routes = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var currency = settings.currency;
    var norm = Kit.normalizeConfig(settings.config);
    var config = norm.config; // invalid settings fall back to the disabled default
    var selected = new Map(); // product id -> { product, variantId, extra }
    var catalogue = {}; // sport collection handles -> Promise of products
    var view = { key: '', products: [], type: '', shown: PAGE_SIZE };
    var submitting = false;
    var stage = 'sport'; // the step on show; setStage() keeps it
    var opensExtras = false; // the dock button asks about accessories instead of adding; update() sets it
    var socks = []; // sock products, listed with every sport's accessories
    var quick = $('[data-kit-quick]');
    var quickProduct = null;
    var quickTrigger = null;
    var descriptions = {}; // product handle -> Promise of plain-text description lines
    var reviewExcerpts = {}; // product handle -> Promise of real, server-rendered Judge.me excerpts

    var el = {
      pills: $('[data-kit-pills]'),
      gridStatus: $('[data-kit-grid-status]'),
      grid: $('[data-kit-grid]'),
      more: $('[data-kit-more]'),
      stages: $('[data-kit-stages]'),
      chosen: $('[data-kit-chosen]'),
      dock: $('[data-kit-dock]'),
      selected: $('[data-kit-selected]'),
      earned: $('[data-kit-earned]'),
      barStandard: $('[data-kit-bar-standard]'),
      nextHint: $('[data-kit-next-hint]'),
      extrasNote: $('[data-kit-extras-note]'),
      extrasEmpty: $('[data-kit-extras-empty]'),
      extrasGrid: $('[data-kit-extras-grid]'),
      summary: $('[data-kit-summary]'),
      summaryBar: $('[data-kit-summary] > summary'),
      count: $('[data-kit-count]'),
      barTotal: $('[data-kit-bar-total]'),
      progress: $('[data-kit-progress]'),
      compactProgress: $('[data-kit-compact-progress]'),
      lines: $('[data-kit-lines]'),
      empty: $('[data-kit-empty]'),
      total: $('[data-kit-total]'),
      savingRow: $('[data-kit-saving-row]'),
      saving: $('[data-kit-saving]'),
      payableRow: $('[data-kit-payable-row]'),
      payable: $('[data-kit-payable]'),
      note: $('[data-kit-note]'),
      error: $('[data-kit-error]'),
      status: $('[data-kit-status]'),
      add: $('[data-kit-add]'),
      skip: $('[data-kit-skip]'),
      review: $('[data-kit-review]')
    };
    var cardTemplate = $('[data-kit-card-template]');
    var lineTemplate = $('[data-kit-line-template]');
    var standardNote = el.note.textContent;
    var extrasNote = el.extrasNote.textContent;
    var draftToggle = el.review && $('[data-kit-draft-toggle]', el.review);
    // Preview only (theme editor or an unpublished theme): the owner-confirmed 10/15/20/25
    // projection as its own enabled config. The merchant config is never switched on from
    // here and checkout still charges standard prices.
    var draftConfig = el.review && {
      version: 1,
      enabled: true,
      currency: config.currency,
      minimumUnitPrice: config.minimumUnitPrice,
      tiers: JSON.parse(el.review.getAttribute('data-kit-draft-tiers'))
    };
    var rules = config; // what the summary is worked out with; update() sets it
    var rewarding = false; // a tier of `rules` has a reward that can show in this currency
    var rewarded = {}; // product id -> true where the reached tier discounts that line

    var formatter;
    try {
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: currency });
    } catch (error) {
      formatter = { format: function (amount) { return amount.toFixed(2) + ' ' + (currency || ''); } };
    }

    function money(minor) {
      return formatter.format(minor / 100);
    }

    function getJSON(url) {
      return fetch(url, { credentials: 'same-origin' }).then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      }).then(function (text) {
        // The layout-none collection view is served as HTML: parse only the JSON object.
        return JSON.parse(text.slice(text.indexOf('{'), text.lastIndexOf('}') + 1));
      });
    }

    function kitError(message) {
      var error = new Error(message);
      error.kit = true;
      return error;
    }

    function setError(message) {
      el.error.textContent = message;
      el.error.hidden = !message;
    }

    /* Catalogue */

    // ponytail: loads every page of a sport up front (every collection of the sport, in parallel); stream pages if a sport outgrows that.
    function loadCollection(handle, query) {
      var base = routes + 'collections/' + encodeURIComponent(handle) + '?' + (query || '') + 'view=kit-products&page=';
      return getJSON(base + 1).then(function (first) {
        var rest = [];
        for (var page = 2; page <= first.pages; page++) rest.push(getJSON(base + page));
        return Promise.all(rest).then(function (pages) {
          return [first].concat(pages).map(function (data) { return data.products; });
        });
      });
    }

    function loadSport(key) {
      if (!catalogue[key]) {
        var handles = key.split(',').map(function (handle) { return handle.trim(); }).filter(Boolean);
        var failed = 0;
        catalogue[key] = Promise.all(handles.map(function (handle) {
          // A missing collection is skipped so the rest of the sport still loads.
          return loadCollection(handle).catch(function () {
            failed++;
            return [];
          });
        })).then(function (lists) {
          if (failed === handles.length) {
            delete catalogue[key];
            throw new Error('catalogue unavailable');
          }
          return Kit.mergeProducts([].concat.apply([], lists));
        });
      }
      return catalogue[key];
    }

    function inStock(product) {
      return product.variants.some(function (variant) { return variant.available && Kit.variantRole(product, variant, config, currency) === 'kit'; });
    }

    // Kit or accessory is decided per variant by the shared rule, so one product can be both.
    function hasRole(product, role) {
      return product.variants.some(function (variant) {
        return Kit.variantRole(product, variant, config, currency) === role;
      });
    }

    // The sport's lead category first, then the shared type order. In-stock products lead each
    // category; the sort is stable, so the merchant's order holds inside those groups.
    function arrange(products, lead) {
      var order = [lead].concat(Kit.TRAINING_TYPES);
      return products.slice().sort(function (a, b) {
        return order.indexOf(a.type) - order.indexOf(b.type) || inStock(b) - inStock(a);
      });
    }

    // Stages: sport, kit, accessories. Panels are only shown and hidden, so cards, chosen
    // options and the selection all survive going back and forth.
    function setStage(name) {
      stage = name;
      root.querySelectorAll('[data-kit-stage]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-kit-stage') !== name;
      });
      el.stages.querySelectorAll('button').forEach(function (button) {
        if (button.getAttribute('data-kit-go') === name) button.setAttribute('aria-current', 'step');
        else button.removeAttribute('aria-current');
      });
      // The step nav and the kit dock only appear once a sport has been chosen.
      el.stages.hidden = !view.key;
      el.dock.hidden = name === 'sport' || !view.key;
      $('[data-kit-stage="' + name + '"] [data-kit-stage-title]').focus();
      $('[data-kit-stage="' + name + '"] [data-kit-stage-title]').scrollIntoView({ block: 'start' });
    }

    function showSport(tab) {
      var key = tab.getAttribute('data-kit-sport');
      var dropped = 0;
      if (key === view.key && view.products.length) {
        setStage('kit'); // same sport again: back to the kit exactly as it was
        update(); // the dock button follows the step
        return;
      }
      if (key !== view.key) {
        selected.forEach(function (entry) {
          if (!entry.extra) dropped++;
        });
        // A new sport is a new catalogue, so picked kit goes: ask first. Accessories always stay.
        if (dropped && !window.confirm('Change sport? This clears the ' + dropped + (dropped === 1 ? ' kit item' : ' kit items') + ' you have picked. Accessories stay in your kit.')) return;
        selected.forEach(function (entry, id) {
          if (!entry.extra) selected.delete(id);
        });
      }
      root.querySelectorAll('[data-kit-sport]').forEach(function (button) {
        button.setAttribute('aria-pressed', String(button === tab));
      });
      view = { key: key, lead: tab.getAttribute('data-kit-lead') || '', products: [], type: '', shown: PAGE_SIZE };
      el.chosen.textContent = $('[data-kit-sport-label]', tab).textContent;
      el.grid.textContent = '';
      el.pills.textContent = '';
      el.more.hidden = true;
      renderExtras(); // the last sport's unpicked accessories go, picked ones stay
      el.gridStatus.textContent = 'Loading kit…';
      setStage('kit');
      update();
      // update() clears the status line while anything is selected, so announce after it.
      if (dropped) el.status.textContent = 'Kit cleared for your new sport.' + (selected.size ? ' Your accessories are still in your kit.' : '');
      loadSport(key).then(function (products) {
        if (view.key !== key) return; // another sport was picked while this one loaded
        // ponytail: No Gi is known by its first handle, no-gi. It also lists bjj-no-gi, which holds
        // gis and belts on the store, so only no-gi kit types are kept. Make the type list a
        // sport block setting if another sport needs one. The merged BJJ card leads with bjj-kimonos,
        // so it is never filtered: this only serves a standalone No Gi block.
        if (key.split(',')[0].trim() === 'no-gi') {
          products = products.filter(function (product) {
            return ['Rash Guards', 'Fight Shorts', 'Compression Shorts', 'Spats', 'Grappling Dummies'].indexOf(product.type) > -1;
          });
        }
        view.extras = products;
        renderExtras();
        // Kit is only what has a kit option: hand wraps and anything wholly under the floor are accessories.
        products = products.filter(function (product) { return hasRole(product, 'kit'); });
        view.products = arrange(products, view.lead);
        renderPills();
        renderGrid(false);
      }, function () {
        if (view.key === key) el.gridStatus.textContent = 'We could not load this kit. Check your connection and pick the sport again.';
      });
    }

    function renderPills() {
      el.pills.textContent = '';
      // view.products is already in category order, the sport's lead category first.
      var types = [];
      view.products.forEach(function (product) {
        if (types.indexOf(product.type) < 0) types.push(product.type);
      });
      if (types.length < 2) return;
      [''].concat(types).forEach(function (type) {
        var pill = document.createElement('button');
        pill.type = 'button';
        pill.className = 'ftk__pill';
        pill.setAttribute('data-kit-filter', type);
        pill.setAttribute('aria-pressed', String(type === view.type));
        // The count is every product in the category, sold out included.
        pill.textContent = (type || 'All kit') + ' (' + view.products.filter(function (product) {
          return !type || product.type === type;
        }).length + ')';
        el.pills.appendChild(pill);
      });
    }

    function renderGrid(append) {
      var list = view.products.filter(function (product) {
        return !view.type || product.type === view.type;
      });
      // A chosen category lists its whole group from every loaded page; only the mixed list pages in 24s.
      var limit = view.type ? list.length : view.shown;
      var stocked = list.filter(inStock).length;
      var from = append ? el.grid.children.length : 0;
      if (!append) el.grid.textContent = '';
      list.slice(from, limit).forEach(function (product) {
        el.grid.appendChild(card(product, false));
      });
      el.more.hidden = list.length <= limit;
      el.gridStatus.textContent = list.length
        ? 'Showing ' + Math.min(limit, list.length) + ' of ' + list.length + ' ' + (view.type || 'kit products') + '. ' +
          stocked + ' in stock' + (stocked < list.length ? ', ' + (list.length - stocked) + ' sold out' : '') + '.'
        : 'No training kit found here yet.';
      if (append && el.grid.children[from]) $('[data-kit-title]', el.grid.children[from]).focus();
    }

    // Accessories: picked extras first, then the sport's accessory options (hand wraps and
    // anything under the price floor), then socks. One card per product.
    function renderExtras() {
      var seen = {};
      var list = [];
      selected.forEach(function (entry) {
        if (entry.extra) list.push(entry.product);
      });
      list = list.concat(view.extras || [], socks).filter(function (product) {
        if (seen[product.id] || !hasRole(product, 'accessory')) return false;
        return (seen[product.id] = true);
      });
      el.extrasGrid.textContent = '';
      list.forEach(function (product) {
        el.extrasGrid.appendChild(card(product, true));
      });
      el.extrasEmpty.hidden = list.length > 0;
    }

    // Socks come from the native Socks type feed, which carries review data. A handle that
    // feed does not hold falls back to the product endpoint, without reviews.
    function loadSocks(handles) {
      if (!handles.length) return;
      loadCollection('types', 'q=Socks&').then(Kit.mergeProducts, function () { return []; }).then(function (feed) {
        return Promise.all(handles.map(function (handle) {
          var found = feed.filter(function (product) { return product.handle === handle; })[0];
          return found || getJSON(routes + 'products/' + encodeURIComponent(handle) + '.js').then(function (data) {
            return Kit.fromAjaxProduct(data, routes);
          }, function () { return null; }); // optional extras: a missing product just does not render
        }));
      }).then(function (products) {
        socks = products.filter(Boolean);
        renderExtras();
      });
    }

    /* Cards and summary lines */

    function fillVariants(select, product, variantId, extra) {
      var want = extra ? 'accessory' : 'kit';
      var firstAvailable = null;
      select.textContent = '';
      product.variants.forEach(function (variant) {
        // A product can straddle the price floor: each list only offers its own side, plus the
        // option already picked so a refreshed price never swaps it silently.
        if (String(variant.id) !== String(variantId) && Kit.variantRole(product, variant, config, currency) !== want) return;
        var option = document.createElement('option');
        option.value = variant.id;
        option.disabled = !variant.available;
        option.textContent = variant.title + (variant.available ? '' : ' (sold out)');
        select.appendChild(option);
        if (variant.available && firstAvailable === null) firstAvailable = variant.id;
      });
      var pick = variantId || firstAvailable || (select.options[0] && select.options[0].value);
      if (pick) select.value = pick;
      select.hidden = select.options.length < 2; // single default variant: nothing to choose
      select.setAttribute('aria-label', 'Option for ' + product.title);
    }

    function setImage(image, product, variant) {
      var src = (variant && variant.image) || product.image;
      image.hidden = !src;
      if (src && image.getAttribute('src') !== src) image.src = src;
    }

    // Sale kit counts towards kit size but is never discounted again.
    var saleNote = 'Sale price · counts towards kit size';

    // A product is picked as kit or as an accessory, never both: a card or line only speaks
    // for the pick made on its own side.
    function entryFor(node) {
      var entry = selected.get(node.kitProduct.id);
      return entry && entry.extra === node.kitExtra ? entry : null;
    }

    function card(product, extra) {
      var node = cardTemplate.content.firstElementChild.cloneNode(true);
      var entry;
      var reviews = Kit.reviewSummary(product.rating, product.reviews);
      var title = $('[data-kit-title]', node);
      node.kitProduct = product;
      node.kitExtra = Boolean(extra);
      entry = entryFor(node);
      $('[data-kit-reviews]', node).hidden = !reviews;
      $('[data-kit-reviews]', node).textContent = reviews;
      $('[data-kit-link]', node).href = product.url;
      title.href = product.url;
      title.textContent = product.title;
      $('[data-kit-type]', node).textContent = extra ? 'Optional extra' : product.type;
      fillVariants($('[data-kit-variant]', node), product, entry && entry.variantId, extra);
      syncCard(node);
      return node;
    }

    function syncCard(node) {
      var product = node.kitProduct;
      var select = $('[data-kit-variant]', node);
      var entry = entryFor(node);
      if (entry) select.value = entry.variantId;
      var variant = Kit.findVariant(product, select.value);
      var soldOut = !variant || !variant.available;
      var sale = Boolean(variant) && Kit.isSale({ price: variant.price, compareAtPrice: variant.compare_at_price });
      var compare = $('[data-kit-compare]', node);
      var flag = $('[data-kit-flag]', node);
      var toggle = $('[data-kit-toggle]', node);

      $('[data-kit-price]', node).textContent = variant ? money(variant.price) : '';
      compare.hidden = !sale;
      compare.textContent = sale ? money(variant.compare_at_price) : '';
      // Only sale kit that counts says so: an extra or an option under the price floor never does.
      flag.hidden = !sale || !Kit.qualifies(Kit.toLine(product, variant.id, node.kitExtra, config, currency), rules, currency);
      flag.textContent = saleNote;
      setImage($('[data-kit-img]', node), product, variant);

      toggle.disabled = soldOut && !entry;
      toggle.textContent = entry ? 'Remove' : soldOut ? 'Sold out' : node.kitExtra ? 'Add accessory' : 'Add to kit';
      toggle.setAttribute('aria-label', entry
        ? 'Remove ' + product.title + ' from kit'
        : soldOut ? product.title + ' is sold out' : 'Add ' + product.title + ' to kit');
      toggle.classList.toggle('is-selected', Boolean(entry));
      node.classList.toggle('is-selected', Boolean(entry));
    }

    function line(entry) {
      var node = lineTemplate.content.firstElementChild.cloneNode(true);
      var product = entry.product;
      var variant = Kit.findVariant(product, entry.variantId);
      var kitLine = Kit.toLine(product, entry.variantId, entry.extra, config, currency);
      var sale = Boolean(variant) && Kit.isSale({ price: variant.price, compareAtPrice: variant.compare_at_price });
      var flag = $('[data-kit-flag]', node);
      var note = '';
      if (!variant || !variant.available) note = 'Sold out: choose another option';
      else if (entry.extra) note = 'Optional extra' + (rewarded[product.id] ? ': gets your kit discount' : sale && rewarding ? ': stays at sale price' : '');
      else if (!Kit.qualifies(kitLine, rules, currency)) note = 'Does not count towards kit size';
      else if (sale) note = saleNote;

      node.kitProduct = product;
      node.kitExtra = entry.extra;
      setImage($('[data-kit-img]', node), product, variant);
      $('[data-kit-title]', node).textContent = product.title;
      $('[data-kit-price]', node).textContent = variant ? money(variant.price) : '';
      flag.hidden = !note;
      flag.textContent = note;
      fillVariants($('[data-kit-variant]', node), product, entry.variantId, entry.extra);
      $('[data-kit-remove]', node).setAttribute('aria-label', 'Remove ' + product.title + ' from kit');
      return node;
    }

    function renderLines() {
      // Lines are rebuilt, so put focus back where the customer was working.
      var active = document.activeElement;
      var activeLine = active && el.lines.contains(active) ? active.closest('[data-kit-line]') : null;
      var keep = activeLine ? activeLine.kitProduct.id : null;
      el.lines.textContent = '';
      el.selected.textContent = '';
      selected.forEach(function (entry) {
        el.lines.appendChild(line(entry));
        var item = document.createElement('li');
        var button = document.createElement('button');
        var image = document.createElement('img');
        var variant = Kit.findVariant(entry.product, entry.variantId);
        button.type = 'button';
        button.setAttribute('data-kit-edit', entry.product.id);
        button.setAttribute('aria-label', 'Edit ' + entry.product.title + (variant ? ', ' + variant.title : ''));
        button.setAttribute('aria-expanded', String(el.summary.open));
        image.alt = '';
        setImage(image, entry.product, variant);
        // Keep an identifiable button when a product has no photograph.
        if (image.hidden) button.textContent = entry.product.title;
        else button.appendChild(image);
        item.appendChild(button);
        el.selected.appendChild(item);
      });
      if (keep === null) return;
      var again = Array.prototype.filter.call(el.lines.children, function (node) {
        return node.kitProduct.id === keep;
      })[0];
      (again ? $('[data-kit-variant]', again) : el.summaryBar).focus();
    }

    /* Summary */

    function kitLines() {
      var lines = [];
      selected.forEach(function (entry) {
        var kitLine = Kit.toLine(entry.product, entry.variantId, entry.extra, config, currency);
        if (kitLine) lines.push(kitLine);
      });
      return lines;
    }

    // Reward copy only exists when the merchant has enabled valid, non-zero tiers that can
    // apply in this currency, or when the preview projection is the config in use.
    function rewardLabel(tier) {
      if (!Kit.rewardAvailable(rules, tier, currency)) return '';
      return tier.type === 'percentage' ? tier.value + '% off' : money(tier.value) + ' off';
    }

    function renderProgress(result) {
      var count = result.qualifyingCount;
      var text = count + (count === 1 ? ' kit item' : ' kit items');
      el.progress.textContent = '';
      rules.tiers.forEach(function (tier) {
        var step = document.createElement('li');
        var reward = rewardLabel(tier);
        step.className = 'ftk__step' + (count >= tier.count ? ' is-reached' : '');
        step.textContent = tier.count + ' items';
        if (result.tier && result.tier.count === tier.count) step.setAttribute('aria-current', 'true');
        if (reward) {
          var small = document.createElement('small');
          small.textContent = reward;
          step.appendChild(small);
        }
        el.progress.appendChild(step);
      });
      var reached = result.tier ? rewardLabel(result.tier) : '';
      var next = result.nextTier ? rewardLabel(result.nextTier) : '';
      // A tier is only named with a reward these rules can show. The saving itself sits in the totals.
      if (reached) text += ' · ' + reached;
      if (next && stage !== 'extras') text += ' · Add ' + result.remaining + ' more for ' + next;
      // Keep next-tier progress visible with the kit details closed.
      el.compactProgress.textContent = text;
      el.earned.hidden = !reached;
      el.earned.textContent = reached;
    }

    function update() {
      var lines = kitLines();
      // Preview projection while the review toggle is on, otherwise the merchant config.
      rules = !config.enabled && draftToggle && draftToggle.checked ? draftConfig : config;
      var result = Kit.evaluateKit(lines, rules, currency);
      var size = selected.size;
      var blocked = false;
      var picked = 0; // kit picks; the rest of the selection is accessories
      selected.forEach(function (entry) {
        var variant = Kit.findVariant(entry.product, entry.variantId);
        if (!variant || !variant.available) blocked = true;
        if (!entry.extra) picked++;
      });
      var canExtras = picked >= 1; // offer extras in every currency; earning a discount stays separate

      rewarding = rules.tiers.some(function (tier) { return Kit.rewardAvailable(rules, tier, currency); });
      rewarded = {};
      if (result.applied) result.targets.forEach(function (unit) { rewarded[unit.productId] = true; });
      el.count.textContent = size + (size === 1 ? ' item' : ' items');
      el.barStandard.hidden = !result.applied;
      el.barStandard.textContent = money(result.standardTotal);
      el.barTotal.textContent = money(result.payable);
      el.total.textContent = money(result.standardTotal);
      el.savingRow.hidden = el.payableRow.hidden = !result.applied;
      el.saving.textContent = '−' + money(result.discount);
      el.payable.textContent = money(result.payable);
      // The one pricing caveat. A live discount is only claimed once it actually applies.
      el.note.textContent = rules !== config ? 'Preview only — discounts are not active at checkout.'
        : result.applied ? 'Discount applied at checkout.' : standardNote;
      // Only a rate these rules can show is named: the tier reached, else the next one to unlock.
      // A fixed amount is not a rate accessories share, so it gets the plain note.
      var earned = result.tier ? rewardLabel(result.tier) : '';
      var next = result.nextTier ? rewardLabel(result.nextTier) : '';
      el.extrasNote.textContent = earned && result.tier.type === 'percentage'
        ? 'Add accessories at the same ' + result.tier.value + '% discount. Sale items excluded.'
        : !earned && next
          ? extrasNote + ' Add ' + (result.remaining === 1 ? 'another kit item' : result.remaining + ' more kit items') + ' to unlock ' + next + '.'
          : extrasNote;
      el.nextHint.hidden = canExtras;
      root.querySelectorAll('[data-kit-go="extras"]').forEach(function (button) { button.disabled = !canExtras; });
      el.empty.hidden = size > 0;
      // Kit step only opens accessories; the accessories step submits the selected basket.
      opensExtras = stage !== 'extras' && canExtras;
      el.add.textContent = submitting ? 'Opening checkout…' : stage !== 'extras' ? 'Next: accessories' : 'Continue to checkout';
      el.add.disabled = el.skip.disabled = !picked || blocked || submitting;
      if (size) el.status.textContent = '';

      renderProgress(result);
      renderLines();
      root.querySelectorAll('[data-kit-card]').forEach(function (node) { syncCard(node); });
    }

    /* Quick view */

    // The description is parsed as an inert document: nothing in it runs or is inserted, only
    // its text is read, one line per block.
    function plainLines(html) {
      var doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
      doc.body.querySelectorAll('script, style').forEach(function (node) { node.remove(); });
      doc.body.querySelectorAll('p, li, br, div, tr, h1, h2, h3, h4, h5, h6').forEach(function (node) { node.after('\n'); });
      return doc.body.textContent.split('\n').map(function (text) { return text.trim(); }).filter(Boolean);
    }

    // ponytail: one description request per product per visit, from the product endpoint the
    // stock refresh already uses. A failed request is forgotten so Try again can ask once more.
    function loadDescription(product) {
      if (!descriptions[product.handle]) {
        descriptions[product.handle] = getJSON(routes + 'products/' + encodeURIComponent(product.handle) + '.js').then(function (data) {
          return plainLines(data.description);
        });
        descriptions[product.handle].catch(function () { delete descriptions[product.handle]; });
      }
      return descriptions[product.handle];
    }

    function fillQuick() {
      var product = quickProduct;
      var text = $('[data-kit-quick-text]', quick);
      var status = $('[data-kit-quick-status]', quick);
      var retry = $('[data-kit-quick-retry]', quick);
      text.textContent = '';
      retry.hidden = true;
      status.textContent = 'Loading description…';
      loadDescription(product).then(function (lines) {
        if (quickProduct !== product) return;
        status.textContent = lines.length ? '' : 'No description for this product yet.';
        lines.forEach(function (copy) {
          var paragraph = document.createElement('p');
          paragraph.textContent = copy;
          text.appendChild(paragraph);
        });
      }, function () {
        if (quickProduct !== product) return;
        status.textContent = 'We could not load this description. Check your connection and try again.';
        retry.hidden = false;
      });
    }

    // Fetch only when opening a reviewed product. The PDP already server-renders Judge.me;
    // reuse its first page as text rather than a separate reviews API or untrusted HTML.
    function showReviews(product) {
      var panel = $('[data-kit-quick-review-section]', quick);
      var list = $('[data-kit-quick-review-list]', panel);
      panel.hidden = !(Number(product.reviews) > 0);
      list.textContent = panel.hidden ? '' : 'Loading customer reviews…';
      if (panel.hidden) return;
      if (!reviewExcerpts[product.handle]) {
        reviewExcerpts[product.handle] = fetch(product.url, { credentials: 'same-origin' }).then(function (response) {
          if (!response.ok) throw new Error('Reviews unavailable');
          return response.text();
        }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          return Array.prototype.slice.call(doc.querySelectorAll('#judgeme_product_reviews .jdgm-rev'), 0, 3).map(function (review) {
            return {
              score: review.querySelector('.jdgm-rev__rating') && review.querySelector('.jdgm-rev__rating').getAttribute('data-score'),
              title: review.querySelector('.jdgm-rev__title') && review.querySelector('.jdgm-rev__title').textContent.trim(),
              body: review.querySelector('.jdgm-rev__body') && review.querySelector('.jdgm-rev__body').textContent.trim(),
              author: review.querySelector('.jdgm-rev__author') && review.querySelector('.jdgm-rev__author').textContent.trim()
            };
          });
        });
        reviewExcerpts[product.handle].catch(function () { delete reviewExcerpts[product.handle]; });
      }
      reviewExcerpts[product.handle].then(function (reviews) {
        if (quickProduct !== product) return;
        list.textContent = '';
        if (!reviews.length) { list.textContent = 'Read customer reviews on the product page.'; return; }
        reviews.forEach(function (review) {
          var block = document.createElement('blockquote');
          var score = Number(review.score);
          var heading = document.createElement('strong');
          heading.textContent = (score >= 1 && score <= 5 ? score + '/5 · ' : '') + (review.title || 'Customer review');
          block.appendChild(heading);
          if (review.body) {
            var body = document.createElement('p');
            body.textContent = review.body;
            block.appendChild(body);
          }
          if (review.author) {
            var byline = document.createElement('footer');
            byline.textContent = review.author;
            block.appendChild(byline);
          }
          list.appendChild(block);
        });
      }, function () {
        if (quickProduct === product) list.textContent = 'Reviews could not load. View them on the product page.';
      });
    }

    // The card's own picker and button stay the only way to choose: this just shows the product.
    function openQuick(node) {
      var product = node.kitProduct;
      var variant = Kit.findVariant(product, $('[data-kit-variant]', node).value);
      var reviews = Kit.reviewSummary(product.rating, product.reviews);
      quickProduct = product;
      quickTrigger = $('[data-kit-title]', node);
      setImage($('[data-kit-quick-img]', quick), product, variant);
      $('[data-kit-quick-title]', quick).textContent = product.title;
      $('[data-kit-quick-price]', quick).textContent = variant ? money(variant.price) : '';
      $('[data-kit-quick-reviews]', quick).hidden = !reviews;
      $('[data-kit-quick-reviews]', quick).textContent = reviews;
      $('[data-kit-quick-link]', quick).href = product.url;
      fillQuick();
      showReviews(product);
      quick.showModal();
    }

    /* Cart */

    // Best-effort stock and price check before adding. The cart request still validates stock.
    function refresh() {
      var changes = [];
      var checks = [];
      selected.forEach(function (entry) {
        var url = routes + 'products/' + encodeURIComponent(entry.product.handle) + '.js';
        checks.push(getJSON(url).then(function (data) {
          var latest = Kit.fromAjaxProduct(data, routes);
          var change = Kit.variantChange(Kit.findVariant(entry.product, entry.variantId), Kit.findVariant(latest, entry.variantId));
          entry.product.variants = latest.variants;
          if (change === 'unavailable') changes.push(entry.product.title + ' has sold out in that option. Choose another or remove it.');
          if (change === 'price') changes.push(entry.product.title + ' has a new price. Check your kit, then add again.');
        }, function () {}));
      });
      return Promise.all(checks).then(function () { return changes; });
    }

    function addKit(kitOnly) {
      if (submitting || !selected.size) return; // no double submission
      // No thanks: accessories picked so far are dropped and the kit goes to the cart on its own.
      if (kitOnly) {
        selected.forEach(function (entry, id) {
          if (entry.extra) selected.delete(id);
        });
      }
      submitting = true;
      setError('');
      el.add.setAttribute('aria-busy', 'true');
      update();

      refresh().then(function (changes) {
        if (changes.length) throw kitError(changes.join(' '));
        // One normal add of the real variants. The existing cart is left as it is.
        return fetch(routes + 'cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: Kit.cartItems(kitLines()) })
        });
      }).then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw kitError(String(data.description || data.message || 'Your kit could not be added.'));
        });
      }).then(function () {
        // Keep submission locked until navigation; a slow checkout must not add the kit twice.
        selected.clear();
        window.location.assign(routes + 'checkout');
      }).catch(function (error) {
        setError(error.kit ? error.message : 'We could not add your kit. Check your connection and try again.');
        submitting = false;
        el.add.removeAttribute('aria-busy');
        update();
      });
    }

    /* Events */

    root.addEventListener('click', function (event) {
      var button = event.target.closest('button');
      var opener = event.target.closest('a[data-kit-link], a[data-kit-title]');
      if (opener && root.contains(opener)) {
        // Product image and name open the quick view. A modified click, or a browser without
        // dialog, keeps the normal product link.
        if (!quick.showModal || event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        openQuick(opener.closest('[data-kit-card]'));
        return;
      }
      if (event.target === quick) quick.close(); // a click on the backdrop
      if (event.target.closest('[data-kit-hero-cta]')) setStage('sport'); // the anchor still scrolls natively
      if (!button || !root.contains(button)) return;

      if (button.hasAttribute('data-kit-sport')) {
        showSport(button);
      } else if (button.hasAttribute('data-kit-columns-toggle')) {
        root.setAttribute('data-kit-columns', button.getAttribute('data-kit-columns-toggle'));
        root.querySelectorAll('[data-kit-columns-toggle]').forEach(function (toggle) {
          toggle.setAttribute('aria-pressed', String(toggle === button));
        });
      } else if (button.hasAttribute('data-kit-edit')) {
        el.summary.open = true;
        var id = button.getAttribute('data-kit-edit');
        var editLine = Array.prototype.find.call(el.lines.children, function (node) {
          return String(node.kitProduct.id) === id;
        });
        if (editLine) requestAnimationFrame(function () {
          var picker = $('[data-kit-variant]', editLine);
          (picker.hidden ? $('[data-kit-remove]', editLine) : picker).focus({ preventScroll: true });
          // Scroll only the existing details body, not the catalogue behind the dock.
          editLine.parentElement.parentElement.scrollTop = editLine.offsetTop - editLine.parentElement.parentElement.offsetTop;
        });
      } else if (button.hasAttribute('data-kit-go')) {
        setStage(button.getAttribute('data-kit-go'));
        update(); // the dock button follows the step
      } else if (button.hasAttribute('data-kit-filter')) {
        view.type = button.getAttribute('data-kit-filter');
        view.shown = PAGE_SIZE;
        el.pills.querySelectorAll('button').forEach(function (pill) {
          pill.setAttribute('aria-pressed', String(pill === button));
        });
        renderGrid(false);
      } else if (button === el.more) {
        view.shown += PAGE_SIZE;
        renderGrid(true);
      } else if (button.hasAttribute('data-kit-toggle')) {
        var node = button.closest('[data-kit-card]');
        var product = node.kitProduct;
        // One unit per product: adding again is a removal, sizes change through the select.
        if (entryFor(node)) selected.delete(product.id);
        else selected.set(product.id, { product: product, variantId: Number($('[data-kit-variant]', node).value), extra: node.kitExtra });
        setError('');
        update();
      } else if (button.hasAttribute('data-kit-remove')) {
        selected.delete(button.closest('[data-kit-line]').kitProduct.id);
        setError('');
        update();
      } else if (button.hasAttribute('data-kit-quick-close')) {
        quick.close();
      } else if (button.hasAttribute('data-kit-quick-retry')) {
        fillQuick();
        $('[data-kit-quick-close]', quick).focus(); // the retry button hides itself
      } else if (button === el.skip) {
        addKit(true);
      } else if (button === el.add) {
        // Next opens accessories without writing to the cart; only the final checkout action submits.
        if (opensExtras) {
          // Phones: fold the open kit panel away so the accessories question is on show.
          if (window.matchMedia('(max-width: 989px)').matches) el.summary.open = false;
          setStage('extras');
          update();
        } else {
          addKit();
        }
      }
    });

    root.addEventListener('change', function (event) {
      var target = event.target;
      if (target.hasAttribute('data-kit-variant')) {
        var entry = entryFor(target.closest('[data-kit-card], [data-kit-line]'));
        if (entry) entry.variantId = Number(target.value);
        setError('');
      }
      update();
    });

    el.summary.addEventListener('toggle', function () {
      el.selected.querySelectorAll('button').forEach(function (button) {
        button.setAttribute('aria-expanded', String(el.summary.open));
      });
    });
    quick.addEventListener('close', function () {
      // Back to the card it was opened from. The kit itself was never touched.
      var trigger = quickTrigger;
      quickProduct = quickTrigger = null;
      // Native dialog restoration can run after close; restore the card on the next frame.
      requestAnimationFrame(function () {
        if (trigger && root.contains(trigger)) trigger.focus();
      });
    });
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      submitting = false;
      el.add.removeAttribute('aria-busy');
      update();
    });
    loadSocks((settings.socks || []).map(function (handle) { return handle.trim(); }).filter(Boolean));
    // No sport is preselected: the builder opens on the sport cards with the dock hidden.
    update();
  }

  function boot(scope) {
    scope.querySelectorAll('[data-fumetsu-kit]').forEach(init);
  }

  boot(document);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
